/* Sprint 2R-2C — HUB Inbound scan panel (admin).
 *
 * Designed for keyboard barcode scanners: auto-focus input, submit on Enter,
 * supports manual entry. Shows today's inbound count + recent scans + open
 * exceptions inline.
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, Scan, AlertTriangle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

export default function HubInboundPanel() {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [lastResult, setLastResult] = React.useState(null);
  const [todayItems, setTodayItems] = React.useState([]);
  const [exceptions, setExceptions] = React.useState([]);
  const [damaged, setDamaged] = React.useState(false);
  const [note, setNote] = React.useState("");

  const refresh = React.useCallback(async () => {
    try {
      const today = await adminApi().get("/admin/hub/inbound/today");
      setTodayItems(today.data.items || []);
      const ex = await adminApi().get("/admin/hub/inbound/exceptions?status=open");
      setExceptions(ex.data.items || []);
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    refresh();
    inputRef.current?.focus();
  }, [refresh]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = inputRef.current?.value?.trim();
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/hub/inbound/scan", {
        label_number: v,
        damaged,
        note: note || undefined,
      });
      setLastResult(data);
      if (data.matched && data.kind === "received") {
        toast.success(`Modtaget: ${data.shipment_number} → ${data.delivery_postal_code} ${data.delivery_city || ""}`);
      } else if (data.kind === "already_at_hub") {
        toast.info("Pakke allerede modtaget");
      } else if (data.kind === "damaged_parcel") {
        toast.warning("Skadet pakke registreret — Gatekeeper notificeret");
      } else {
        toast.error(data.message || "Exception oprettet — se Gatekeeper");
      }
      inputRef.current.value = "";
      setDamaged(false);
      setNote("");
      refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Scan-fejl");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const resolveException = async (id, action) => {
    const reason = prompt(`Begrundelse for "${action}":`);
    if (!reason || reason.length < 4) return;
    let link_to_parcel_id;
    if (action === "link_to_parcel") {
      link_to_parcel_id = prompt("Mål-parcel ID (UUID):");
      if (!link_to_parcel_id) return;
    }
    try {
      await adminApi().post(`/admin/hub/inbound/exceptions/${id}/resolve`,
        { action, reason, link_to_parcel_id });
      toast.success("Exception løst");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke løse");
    }
  };

  return (
    <div className="space-y-6" data-testid="hub-inbound-panel">
      <div>
        <h2 className="font-display font-bold text-xl text-brand-dark flex items-center gap-2">
          <Scan size={20} className="text-brand-red"/> HUB Inbound Scan
        </h2>
        <p className="text-sm text-brand-text2 mt-1">
          Scan eller indtast pakke-label ved ankomst. Brug USB-scanner eller mobil-scanner.
        </p>
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-brand p-6 space-y-3">
        <label className="block text-xs uppercase text-brand-text2">Label / shipment number</label>
        <input
          ref={inputRef}
          type="text"
          autoFocus
          placeholder="Scan barcode eller indtast EMA-YYYY-NNNNNN"
          className="w-full px-4 py-3 border border-brand text-lg font-mono"
          data-testid="hub-inbound-input"
          disabled={busy}
        />
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={damaged} onChange={(e) => setDamaged(e.target.checked)}
                   data-testid="hub-inbound-damaged"/>
            Skadet
          </label>
          <input
            type="text"
            placeholder="Note (valgfri)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="flex-1 px-3 py-2 border border-brand text-sm"
            data-testid="hub-inbound-note"
          />
          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2 bg-brand-red hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="hub-inbound-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={16}/> : "Scan"}
          </button>
        </div>
      </form>

      {/* Last result */}
      {lastResult && (
        <div
          className={`p-4 border ${lastResult.matched && lastResult.kind === "received" ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
          data-testid="hub-inbound-last-result"
        >
          {lastResult.matched && lastResult.kind === "received" ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="text-emerald-600 mt-0.5" size={20}/>
              <div className="text-sm">
                <div className="font-mono font-bold text-base">{lastResult.shipment_number}</div>
                <div className="text-brand-dark mt-1">
                  → <b>{lastResult.delivery_postal_code}</b> {lastResult.delivery_city}
                  {lastResult.delivery_zone_name && ` · Zone ${lastResult.delivery_zone_name}`}
                </div>
                {lastResult.recipient_name && <div className="text-brand-text2">Modtager: {lastResult.recipient_name}</div>}
                {lastResult.product && <div className="text-brand-text2">Service: {lastResult.product}</div>}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-600 mt-0.5" size={20}/>
              <div className="text-sm">
                <div className="font-semibold">{lastResult.message}</div>
                {lastResult.exception_id && (
                  <div className="text-xs font-mono text-brand-text2 mt-1">Exception: {lastResult.exception_id}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today */}
        <div className="bg-white border border-brand p-4">
          <h3 className="font-display font-bold text-base text-brand-dark mb-3 flex items-center gap-2">
            <Package size={16}/> I dag ({todayItems.length})
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="hub-inbound-today-list">
            {todayItems.length === 0 ? (
              <div className="text-sm text-brand-text2">Ingen scans endnu</div>
            ) : (
              todayItems.map((e) => (
                <div key={e.id} className="text-sm border-b border-gray-100 pb-1">
                  <div className="font-mono text-xs">{e.parcel_id?.slice(0,8)}...</div>
                  <div className="text-xs text-brand-text2">
                    {new Date(e.ts_iso).toLocaleTimeString("da-DK")} · {e.kind}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Exceptions */}
        <div className="bg-white border border-brand p-4">
          <h3 className="font-display font-bold text-base text-brand-dark mb-3 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600"/> Åbne exceptions ({exceptions.length})
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="hub-inbound-exceptions-list">
            {exceptions.length === 0 ? (
              <div className="text-sm text-brand-text2">Ingen åbne exceptions 🎉</div>
            ) : (
              exceptions.map((ex) => (
                <div key={ex.id} className="text-sm border-b border-gray-100 pb-2"
                     data-testid={`hub-exception-${ex.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">{ex.label_number}</span>
                    <span className="text-[10px] uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{ex.kind}</span>
                  </div>
                  {ex.note && <div className="text-xs text-brand-text2 mt-1">{ex.note}</div>}
                  <div className="text-xs text-brand-text2 mt-1">
                    Scannet af {ex.scanned_by} · {new Date(ex.scanned_at_iso).toLocaleString("da-DK")}
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button onClick={() => resolveException(ex.id, "accept")}
                            className="text-xs px-2 py-1 bg-white border border-brand hover:bg-brand-bg2">Accepter</button>
                    <button onClick={() => resolveException(ex.id, "return_to_sender")}
                            className="text-xs px-2 py-1 bg-white border border-brand hover:bg-brand-bg2">Retur til afsender</button>
                    {ex.kind === "unknown_label" && (
                      <button onClick={() => resolveException(ex.id, "link_to_parcel")}
                              className="text-xs px-2 py-1 bg-white border border-brand hover:bg-brand-bg2">Link til pakke</button>
                    )}
                    <button onClick={() => resolveException(ex.id, "discard")}
                            className="text-xs px-2 py-1 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">Kasser</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
